import { Injectable, Injector, inject } from '@angular/core';
import { AnyRecommendee } from '@app/recommendations/recommendations.model';
import { Tag } from '@app/tags/tag-api.model';
import { InputContext } from '@app/user-content/user-input-v2/input.model';
import {
  APIParameterModel,
  APIResponseModel,
  AssessmentFormDataModel,
  ViewModel,
} from '@app/user-content/user-input-v2/inputs/assessment/assessment.model';
import { AssessmentMapper } from '@app/user-content/user-input-v2/inputs/assessment/services/assessment-mapper.service';
import { AssessmentService } from '@app/user-content/user-input-v2/inputs/assessment/services/assessment.service';
import { addOwnerDetailsToInput } from '@app/user-content/user-input-v2/utils/content-owner-helper';
import { RenderMode } from '@app/user-content/user-input/user-input.model';
import { UserGroupListService } from '@app/user/services/user-group-list.service';
import {
  AssessContentSkills,
  type ContentSkillRequest,
  type ContentSkillResponse,
} from '@degreed/lxp/assess/services';
import { InputTypes, LDFlagsService } from '@degreed/lxp/services';
import { AuthService } from '@degreed/lxp/services/authorization';
import {
  CATALOG_INPUT_TYPE,
  CatalogContentApiService,
  type CreateUpdateCatalogAssessmentRequest,
  type GetCatalogAssessmentResponse,
} from '@degreed/lxp/services/content';
import { Observable, map, switchMap } from 'rxjs';

/**
 * `AssessContentSkills` (`libs/lxp/features/assess/services`) concrete adapter — bound once,
 * at the app root (`app.module.ts`, next to `AssessContentActions`). Branches per call on
 * `isLocalizationEnabled`, exactly like `ContentDetailContextService`/`ContentTabsLayoutComponent`/
 * `ContentPageComponent` already do, so the editor never issues a catalog-content call that
 * 401s (`InputsController.GetContentDetails`'s own gate; the platform's global
 * `AuthInterceptor` treats any 401 as session loss and logs the user out — see this seam's own
 * class doc for the full story):
 *
 * - Localized org: the platform's catalog-content plumbing (`CatalogContentApiService`),
 *   exactly what `AssessmentSkillsStore` used to do directly before this seam existed.
 * - Non-localized org: the legacy assessment edit flow's own read/write (`AssessmentService`
 *   `/inputs/getassessment`+`/inputs/updateassessment`, mapped through `AssessmentMapper`) — the
 *   same plumbing the pre-Assess assessment edit modal uses, reused here instead of inventing a
 *   parallel one. `tags` is the only field this adapter changes; every other field on the
 *   assessment is read back from the server and echoed unchanged.
 *
 * `CatalogContentApiService` and `AssessmentService`/`AssessmentMapper` are none of them
 * `providedIn: 'root'` (each expects its hosting form to provide it — `CATALOG_INPUT_TYPE` for
 * the former, nothing extra for the latter two). Rather than adding three more entries to
 * `app.module.ts`'s providers array, both are constructed in a child `Injector` here, the same
 * trick `CatalogContentApiService`'s own class doc recommends for a one-off consumer.
 */
@Injectable()
export class LxpAssessContentSkillsAdapter extends AssessContentSkills {
  private readonly ldFlagsService = inject(LDFlagsService);
  private readonly authService = inject(AuthService);
  private readonly userGroupListService = inject(UserGroupListService);

  private readonly catalogInjector = Injector.create({
    providers: [
      CatalogContentApiService,
      { provide: CATALOG_INPUT_TYPE, useValue: InputTypes.Assessment },
    ],
    parent: inject(Injector),
  });
  private readonly catalogApi = this.catalogInjector.get(
    CatalogContentApiService
  );

  private readonly legacyInjector = Injector.create({
    providers: [AssessmentService, AssessmentMapper],
    parent: inject(Injector),
  });
  private readonly legacyAssessmentService =
    this.legacyInjector.get(AssessmentService);
  private readonly legacyAssessmentMapper =
    this.legacyInjector.get(AssessmentMapper);

  public override load(inputId: number): Observable<ContentSkillResponse[]> {
    return this.isLocalized()
      ? this.catalogApi
          .get<GetCatalogAssessmentResponse>(inputId)
          .pipe(map((details) => details.tags ?? []))
      : this.legacyAssessmentService
          .getAssessment(inputId, true)
          .pipe(map((res) => (res.tags ?? []).map(tagToContentSkillResponse)));
  }

  public override replace(
    inputId: number,
    skills: readonly ContentSkillRequest[]
  ): Observable<ContentSkillResponse[]> {
    return this.isLocalized()
      ? this.replaceLocalized(inputId, skills)
      : this.replaceLegacy(inputId, skills);
  }

  /**
   * Same formula as `@degreed/lxp/content/content-catalog`'s own `isLocalizationEnabled`
   * (and `ContentCatalogMenuService`'s identical getter) — not imported from that lib
   * directly because it's lazy-loaded (dynamic `import()` only, e.g. `content-page.component.ts`)
   * and this adapter is bound eagerly in `app.module.ts`; the lazy-load boundary lint rule
   * forbids a static import of it from eager code.
   */
  private isLocalized(): boolean {
    return (
      this.ldFlagsService.inputs.multilingualContent &&
      !!this.authService.authUser?.defaultOrgInfo?.settings
        ?.enableLocalizationContent
    );
  }

  /** Mirrors exactly what `AssessmentSkillsStore` used to do directly against
   *  `CatalogContentApiService` before this seam existed (see this class's history): `update`
   *  needs the FULL content payload, not just `tags`, so this re-sends fresh details with only
   *  `tags` replaced, then re-reads so the resolved value reflects what the server persisted. */
  private replaceLocalized(
    inputId: number,
    requests: readonly ContentSkillRequest[]
  ): Observable<ContentSkillResponse[]> {
    return this.catalogApi.get<GetCatalogAssessmentResponse>(inputId).pipe(
      switchMap((details) => {
        const request = {
          ...details,
          tags: [...requests],
        } as unknown as CreateUpdateCatalogAssessmentRequest;
        return this.catalogApi.update<CreateUpdateCatalogAssessmentRequest>(
          request
        );
      }),
      switchMap(() =>
        this.catalogApi.get<GetCatalogAssessmentResponse>(inputId)
      ),
      map((details) => details.tags ?? [])
    );
  }

  /** The legacy assessment edit flow's own read/update (`/inputs/getassessment` +
   *  `/inputs/updateassessment`, via `AssessmentMapper`), reused verbatim rather than inventing
   *  a parallel skills-only endpoint. Re-reads afterward (`updateAssessment`'s response is typed
   *  `unknown`) so the resolved value reflects exactly what the server persisted. */
  private replaceLegacy(
    inputId: number,
    requests: readonly ContentSkillRequest[]
  ): Observable<ContentSkillResponse[]> {
    return this.legacyAssessmentService.getAssessment(inputId, true).pipe(
      switchMap((input) =>
        addOwnerDetailsToInput<APIResponseModel>(
          input,
          this.userGroupListService
        )
      ),
      switchMap((input) =>
        this.legacyAssessmentService.updateAssessment(
          this.buildLegacyUpdateParams(input, requests)
        )
      ),
      switchMap(() =>
        this.legacyAssessmentService.getAssessment(inputId, true)
      ),
      map((res) => (res.tags ?? []).map(tagToContentSkillResponse))
    );
  }

  /**
   * Builds the `/inputs/updateassessment` payload the same way the legacy assessment edit
   * modal's own form does (`AssessmentCatalogFacade`/`AssessmentPathPlanFacade`): seed a
   * `ViewModel` from the freshly-read assessment via `AssessmentMapper.toViewModel`, seed a
   * plain `AssessmentFormDataModel` from that view model's own fields (so every field but
   * `tags` round-trips unchanged), then hand both to `AssessmentMapper.buildApiParameters`.
   * `questionsCorrect`/`dateCompleted`/`percentile` are left `undefined` — `buildApiParameters`
   * passes them straight through, and the modal itself never sends real values for them on a
   * CMS edit (`scenario: 'CMS'`, hardcoded by `buildApiParameters`). Only a handful of
   * `AssessmentFormDataModel`'s fields actually drive `buildApiParameters`'s output (see its own
   * destructure) — the rest (`credSparkToggleButton`, `source`, …) are add-only/CredSpark
   * fields this edit path never touches, so they're omitted rather than invented.
   */
  private buildLegacyUpdateParams(
    input: APIResponseModel & { owner: AnyRecommendee },
    requests: readonly ContentSkillRequest[]
  ): APIParameterModel {
    const inputContext: InputContext = {
      isEditing: true,
      isEditingInternalContent: true,
      inputId: input.inputId,
      inputType: 'Assessment',
      organizationId: this.authService.authUser?.defaultOrgId,
      renderMode: RenderMode.ContentCatalog,
    };
    // `toViewModel` only reads `isEditingCredSpark`/`isEditingContentLocally` off this seed
    // (for `hideXxxField` computations this adapter never uses) and spreads the rest from
    // `input` itself; `inputContext` isn't read by either mapper method, carried only because
    // `ViewModel`'s own type requires it.
    const seedViewModel = {
      isEditingCredSpark: false,
      isEditingContentLocally: false,
      inputContext,
    } as ViewModel;

    const viewModel = this.legacyAssessmentMapper.toViewModel(
      input,
      seedViewModel
    );

    // Shaped like the catalog edit form's `form.value`: the global-add-only and
    // pathway-only controls do not exist on that form, so they are undefined here
    // too, and `buildApiParameters` never reads `credSparkToggleButton` or
    // `addToCatalogCheckbox`.
    const formModel: AssessmentFormDataModel = {
      credSparkToggleButton: viewModel.isCredSpark,
      assessmentUrl: viewModel.url,
      title: viewModel.title,
      provider: viewModel.userProviderName,
      description: viewModel.description,
      owner: viewModel.owner,
      skills: requests.map(contentSkillRequestToTag),
      questions: viewModel.questions,
      totalQuestions: viewModel.questions,
      questionsCorrect: undefined,
      dateCompleted: undefined,
      percentile: undefined,
      addToCatalogCheckbox: undefined,
      durationForm: {
        durationHours: viewModel.durationHours,
        durationMinutes: viewModel.durationMinutes,
      },
      advancedSettings: {
        internalItemId: viewModel.externalId,
        language: {
          id: viewModel.language ?? '',
          key: viewModel.language ?? '',
        },
        publishedDate: viewModel.publishDate,
        visibility: {
          groups: viewModel.orgContentMetadata?.groupIds ?? [],
          hideFromCatalog:
            viewModel.orgContentMetadata?.hideFromCatalog ?? false,
        },
      },
    };

    return this.legacyAssessmentMapper.buildApiParameters(
      viewModel,
      formModel,
      false
    );
  }
}

/** `ContentSkillRequest` -> `Tag`, the shape `AssessmentFormDataModel.skills` (and so
 *  `APIParameterModel.tags`) expects. Carries `tagId`/`level` only when the request has one. */
function contentSkillRequestToTag(request: ContentSkillRequest): Tag {
  return {
    ...('tagId' in request && request.tagId !== undefined
      ? { tagId: request.tagId }
      : {}),
    name: request.name,
    title: request.title,
    ...('level' in request &&
    request.level !== null &&
    request.level !== undefined
      ? { level: request.level }
      : {}),
  };
}

/** A `Tag` (from either the legacy `/inputs/getassessment` or `/inputs/updateassessment`
 *  response) -> `ContentSkillResponse`, the seam's own response shape. `Tag`'s own fields are
 *  all optional (it's shared across every caller), so this fills every gap the same way the
 *  original `InputsService.getInputTypeSpecificDetails`-backed mapping did. */
function tagToContentSkillResponse(
  tag: Tag,
  index: number
): ContentSkillResponse {
  const tagId = tag.tagId as number;
  return {
    tagId,
    name: tag.name,
    title: tag.title ?? tag.name,
    position: tag.position ?? index,
    internalUrl: tag.internalUrl ?? '',
    resourceId: tag.resourceId ?? tagId,
    resourceType: tag.resourceType ?? 'Tag',
    isFollowing: tag.isFollowing ?? false,
    isFocused: tag.isFocused ?? false,
    isMentoring: tag.isMentoring ?? false,
    requiredBySkillStandard: false,
    tagOriginType: tag.tagOriginType ?? '',
    ...(tag.level !== undefined ? { level: tag.level } : {}),
  };
}
